import { movies, type Movie } from "@/mocks/movies";

export type SortKey = "updated" | "views" | "rating";

export function getLatestMovies(count?: number): Movie[] {
  const list = [...movies].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
  return typeof count === "number" ? list.slice(0, count) : list;
}

export function getPopularMovies(count?: number): Movie[] {
  const list = [...movies].sort((a, b) => b.views - a.views);
  return typeof count === "number" ? list.slice(0, count) : list;
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((m) => m.featured);
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getRelatedMovies(movie: Movie, count = 4): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => movie.genres.includes(g)).length;
      const regionScore = m.region === movie.region ? 1 : 0;
      return { movie: m, score: genreScore * 2 + regionScore };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, count).map((item) => item.movie);
}

export function filterMovies(params: {
  region?: string;
  year?: string;
  genre?: string;
}): Movie[] {
  return movies.filter((m) => {
    if (params.region && params.region !== "全部" && m.region !== params.region) {
      return false;
    }
    if (params.year && params.year !== "全部") {
      const value = params.year;
      if (value.endsWith("前")) {
        const bound = parseInt(value, 10);
        if (!Number.isNaN(bound) && m.year >= bound) return false;
      } else if (m.year !== parseInt(value, 10)) {
        return false;
      }
    }
    if (params.genre && params.genre !== "全部" && !m.genres.includes(params.genre)) {
      return false;
    }
    return true;
  });
}

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const copy = [...list];
  if (key === "views") return copy.sort((a, b) => b.views - a.views);
  if (key === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function searchMovies(query: string): Movie[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((m) => {
    const haystack = [
      m.title,
      m.region,
      m.director,
      m.summary,
      m.synopsis,
      m.cast.join(" "),
      m.genres.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}